import { useState } from 'react'
import { DOSE_UNITS } from '../lib/peptide.js'

// textarea (one item per line) <-> string[]
const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '')
const fromLines = (str) => str.split('\n').map((s) => s.trim()).filter(Boolean)

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const blank = {
  slug: '', name: '', type: 'peptide', category: '', summary: '', sequence: '', overview: '',
  halfLife: '', reconstitution: '', aka: [], tags: [], mechanism: [],
  researchedUses: [], cautions: [], protocol: { route: '', dose: '', frequency: '', notes: '' },
  calcPreset: null, sortOrder: 100,
}

export default function PeptideForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(() => ({ ...blank, ...initial, protocol: { ...blank.protocol, ...(initial?.protocol || {}) } }))
  const [hasPreset, setHasPreset] = useState(Boolean(initial?.calcPreset))
  const [preset, setPreset] = useState(initial?.calcPreset || { vialMg: '', waterMl: '', dose: '', doseUnit: 'mcg' })
  const [err, setErr] = useState('')

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const setProto = (k, v) => setF((s) => ({ ...s, protocol: { ...s.protocol, [k]: v } }))

  const submit = (e) => {
    e.preventDefault()
    setErr('')
    if (!f.name.trim()) return setErr('Name is required.')
    const slug = (f.slug.trim() || slugify(f.name))
    const payload = {
      ...f,
      slug,
      name: f.name.trim(),
      sortOrder: Number(f.sortOrder) || 100,
      calcPreset: hasPreset
        ? {
            vialMg: Number(preset.vialMg) || 0,
            waterMl: Number(preset.waterMl) || 0,
            dose: Number(preset.dose) || 0,
            doseUnit: preset.doseUnit || 'mcg',
          }
        : null,
    }
    onSave(payload)
  }

  return (
    <form onSubmit={submit} className="card card-pad pep-form">
      <div className="grid-2">
        <Field label="Name *">
          <input className="input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="BPC-157" />
        </Field>
        <Field label="Slug (URL)" hint="auto from name if blank">
          <input className="input" value={f.slug} onChange={(e) => set('slug', e.target.value)} placeholder="bpc-157" />
        </Field>
      </div>

      <div className="grid-2">
        <Field label="Type">
          <select className="select" value={f.type} onChange={(e) => set('type', e.target.value)}>
            <option value="peptide">Peptide</option>
            <option value="amino">Amino</option>
          </select>
        </Field>
        <Field label="Category">
          <input className="input" value={f.category} onChange={(e) => set('category', e.target.value)} placeholder="Recovery & tissue repair" />
        </Field>
      </div>
      <Field label="Sort order" hint="lower = earlier">
        <input className="input" type="number" value={f.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
      </Field>

      <Field label="Summary" hint="short card blurb">
        <textarea className="input" rows={2} value={f.summary} onChange={(e) => set('summary', e.target.value)} />
      </Field>

      <div className="grid-2">
        <Field label="Also known as" hint="one per line">
          <textarea className="input" rows={3} value={toLines(f.aka)} onChange={(e) => set('aka', fromLines(e.target.value))} />
        </Field>
        <Field label="Tags" hint="one per line">
          <textarea className="input" rows={3} value={toLines(f.tags)} onChange={(e) => set('tags', fromLines(e.target.value))} />
        </Field>
      </div>

      <Field label="Sequence">
        <input className="input" value={f.sequence} onChange={(e) => set('sequence', e.target.value)} placeholder="Gly-Glu-Pro-…" />
      </Field>

      <Field label="Overview" hint="main descriptive paragraph">
        <textarea className="input" rows={5} value={f.overview} onChange={(e) => set('overview', e.target.value)} />
      </Field>

      <Field label="How it works" hint="one bullet per line">
        <textarea className="input" rows={4} value={toLines(f.mechanism)} onChange={(e) => set('mechanism', fromLines(e.target.value))} />
      </Field>

      <Field label="Researched uses" hint="one bullet per line">
        <textarea className="input" rows={4} value={toLines(f.researchedUses)} onChange={(e) => set('researchedUses', fromLines(e.target.value))} />
      </Field>

      <div className="divider" />
      <h4 style={{ margin: '0 0 12px' }}>Protocol</h4>
      <div className="grid-2">
        <Field label="Route"><input className="input" value={f.protocol.route} onChange={(e) => setProto('route', e.target.value)} /></Field>
        <Field label="Dose"><input className="input" value={f.protocol.dose} onChange={(e) => setProto('dose', e.target.value)} /></Field>
        <Field label="Frequency"><input className="input" value={f.protocol.frequency} onChange={(e) => setProto('frequency', e.target.value)} /></Field>
        <Field label="Notes"><input className="input" value={f.protocol.notes} onChange={(e) => setProto('notes', e.target.value)} /></Field>
      </div>

      <Field label="Half-life">
        <input className="input" value={f.halfLife} onChange={(e) => set('halfLife', e.target.value)} />
      </Field>
      <Field label="Reconstitution">
        <textarea className="input" rows={3} value={f.reconstitution} onChange={(e) => set('reconstitution', e.target.value)} />
      </Field>
      <Field label="Cautions" hint="one bullet per line">
        <textarea className="input" rows={4} value={toLines(f.cautions)} onChange={(e) => set('cautions', fromLines(e.target.value))} />
      </Field>

      <div className="divider" />
      <label className="row" style={{ gap: 8, cursor: 'pointer', marginBottom: hasPreset ? 14 : 0 }}>
        <input type="checkbox" checked={hasPreset} onChange={(e) => setHasPreset(e.target.checked)} />
        <span className="soft" style={{ fontWeight: 600 }}>Injectable — add a calculator preset</span>
      </label>
      {hasPreset && (
        <div className="grid-2">
          <Field label="Vial (mg)"><input className="input" type="number" step="any" value={preset.vialMg} onChange={(e) => setPreset({ ...preset, vialMg: e.target.value })} /></Field>
          <Field label="Water (mL)"><input className="input" type="number" step="any" value={preset.waterMl} onChange={(e) => setPreset({ ...preset, waterMl: e.target.value })} /></Field>
          <Field label="Dose"><input className="input" type="number" step="any" value={preset.dose} onChange={(e) => setPreset({ ...preset, dose: e.target.value })} /></Field>
          <Field label="Dose unit">
            <select className="select" value={preset.doseUnit} onChange={(e) => setPreset({ ...preset, doseUnit: e.target.value })}>
              {DOSE_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </Field>
        </div>
      )}

      {err && <div className="form-error" style={{ marginTop: 12 }}>{err}</div>}
      <div className="row" style={{ gap: 10, marginTop: 20 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save peptide'}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="field">
      <label className="label">{label} {hint && <span className="hint">({hint})</span>}</label>
      {children}
    </div>
  )
}
